<template>
  <div class="page">
    <loader v-if="dataLoading" style="z-index: 1101" />
    <div v-if="loadError" class="p-error p-3">{{ loadError }}</div>
    <div v-else-if="!dataLoading && !hasVisibleContent" class="p-3">
      Nessun contenuto disponibile per le fatture selezionate.
    </div>

    <Accordion v-else v-model:activeIndex="accordionActiveIndex" :multiple="true">

      <AccordionTab v-if="fatturaNumeriNo.length > 0">
        <template #header>
          <span>Fatture non dilazionabili {{fatturaNumeriNo.length}}</span>
        </template>

          <div class="formgrid grid">
            <div v-for="fattura_numero of fatturaNumeriNo" class="field col-1" :key="fattura_numero">
              <router-link :to="`/ask/clienti/fatture/${fattura_numero}/dettagli`" custom v-slot="{ href }">
                <Button :label="`${fattura_numero}`" class="p-button-info p-button-text" @click="navigateToTab(href)"/>
              </router-link>
            </div>
          </div>
      </AccordionTab>

      <AccordionTab
        v-for="cliente of clientiPronti"
        :key="cliente.cod_cliente"
      >
        <template #header>
          <span>{{ cliente.ragsoc }} - Totale Saldo: {{ formatClienteSaldo(cliente.cod_cliente) }}</span>
        </template>

        <div class="text-center mb-3">FATTURE</div>
        <DataTable
          :value="cliente.fatture"
          :selection="getClienteSelection(cliente.cod_cliente)"
          @update:selection="setClienteSelection(cliente.cod_cliente, $event)"
          dataKey="numero"
          responsiveLayout="scroll"
        >
          <Column selectionMode="multiple" headerStyle="width: 3em"></Column>
          <Column field="numero" header="Numero"></Column>
          <Column header="Metodo di pagamento">
            <template #body="{ data: row }">
              {{ row.payment_method || '-' }}
            </template>
          </Column>
          <Column header="Saldo">
            <template #body="{ data: row }">
              {{ currencyFormatter.format( (row.saldo !== null)? row.saldo: 0 ) }}
            </template>
          </Column>
          <Column header="Data Emissione">
            <template #body="{ data: row }">
              {{ (row.data_emissione !== null)? moment(row.data_emissione).format('DD/MM/YYYY'): '' }}
            </template>
          </Column>
          <Column header="Data Scadenza">
            <template #body="{ data: row }">
              {{ (row.data_scadenza !== null)? moment(row.data_scadenza).format('DD/MM/YYYY'): '' }}
            </template>
          </Column>
          <Column header="">
            <template #body="{ data: row }">
              <div class="pay-method-cell">
                <Button
                  v-if="canChangeToPostalSlip(row)"
                  label="Cambia a Bollettino Postale"
                  class="p-button-sm p-button-outlined pay-method-change-btn"
                  :loading="changingInvoiceNumber === row.numero"
                  :disabled="!!changingInvoiceNumber"
                  @click="askPostalSlipConfirm(row)"
                />
              </div>
            </template>
          </Column>
        </DataTable>

        <div class="formgrid grid">
          <div class="field col-12 md:col-4">
            <label :for="`data_creazione_${cliente.cod_cliente}`">Data Creazione</label>
            <calendar     :id="`data_creazione_${cliente.cod_cliente}`" 
              v-model="statoClienti[cliente.cod_cliente].data_creazione" 

              @keypress.prevent="false"

              :showIcon="true" 
              dateFormat="dd/mm/yy"
              class="inputfield w-full"
            />
          </div>
          <div class="field col-12 md:col-4">
            <label :for="`data_inizio_${cliente.cod_cliente}`">Data Inizio Rate</label>
            <calendar     :id="`data_inizio_${cliente.cod_cliente}`" 
              v-model="statoClienti[cliente.cod_cliente].data_inizio" 

              @keypress.prevent="false"
              @date-select="onChangeDataInizio(cliente.cod_cliente)"

              :showIcon="true" 
              dateFormat="dd/mm/yy"
              class="inputfield w-full"
            />
          </div>

          <div class="field col-12 md:col-4">
            <label :for="`numero_rate_${cliente.cod_cliente}`">Rate</label>
            <InputNumber 
              :id="`numero_rate_${cliente.cod_cliente}`"
              v-model="statoClienti[cliente.cod_cliente].num_rate" 

              @input="onChangeRate(cliente.cod_cliente)"

              :min="1" 
              :max="24"
              
              showButtons 
              buttonLayout="horizontal" 
              :step="1"
              decrementButtonClass="p-button-danger" 
              incrementButtonClass="p-button-success" 

              class="inputfield w-full"
            />
          </div>
        </div>

        <div class="mb-3"></div>
        <DataTable 
          :value="statoClienti[cliente.cod_cliente].rate" 
          editMode="cell"
          @cell-edit-complete="onCellImportoEditComplete($event, cliente.cod_cliente)"
          class="editable-cells-table" 
          responsiveLayout="scroll"
        >
          <Column field="data" header="Data">
            <template #body="{ data: row }">
              {{ (row.data !== null)? moment(row.data).format('DD/MM/YYYY'): '' }}
            </template>
            <template #editor="{ data: row }">
              <calendar 
                v-model="row.data"
                dateFormat="dd/mm/yy"
              />
            </template>
          </Column>
          <Column field="importo" header="Importo">
            <template #body="{ data: row }">
              {{ currencyFormatter.format( (row.importo !== null)? row.importo: 0 ) }}
            </template>
            <template #editor="{ data: row }">
              <InputNumber v-model="row.importo" mode="currency" currency="EUR" locale="it-IT" />
            </template>
          </Column>
        </DataTable>
        <div class="mb-3"></div>
        <div v-if="statoClienti[cliente.cod_cliente].saldo != statoClienti[cliente.cod_cliente].saldo_rate" class="mb-3 p-error">Gli importi totale delle fatture e delle rate non corrispondono</div>

        <div class="p-d-flex p-p-3 card">
          <Button 
            label="Crea Dilazione" 
            icon="pi pi-send" 
            class="m-2"
            :disabled="statoClienti[cliente.cod_cliente].saldo != statoClienti[cliente.cod_cliente].saldo_rate" 
            @click="onCreateNewDilazione(cliente.cod_cliente)" 
          />
          <Button 
            label="Rimuovi Dilazione" 
            icon="pi pi-trash" 
            class="m-2 p-button-danger"
            @click="onRemoveDilazione(cliente.cod_cliente)" 
          />
        </div>
      </AccordionTab>

      <AccordionTab v-if="rimossi.length > 0">
        <template #header>
          <span>Clienti rimossi {{rimossi.length}}</span>
        </template>

        <div v-for="cliente of rimossi" :key="cliente.cod_cliente" class="card">
          <div class="font-bold text-gray-900 m-2 px-5 py-3 border-round">
            <div class="formgrid grid">
              <div class="field col-12">{{ cliente.ragsoc }}</div>
              <div v-for="fattura of cliente.fatture" class="field col-1" :key="fattura.numero">
                <router-link
                  :to="`/ask/clienti/fatture/${fattura.numero}/dettagli`"
                  custom
                  v-slot="{ href }"
                >
                  <Button :label="`${fattura.numero}`" 
                    class="p-button-info p-button-text"
                    @click="navigateToTab(href)" 
                  />
                </router-link>
              </div>
            </div>
            <div class="p-d-flex p-p-3 card">
              <Button 
                label="Ripristina Dilazione" 
                icon="pi pi-file-o" 
                class="p-ml-auto p-button-danger"
                @click="onRestoreDilazione(cliente.cod_cliente)" 
              />
            </div>
          </div>
        </div>
      </AccordionTab>

      <AccordionTab v-if="creati.length > 0">
        <template #header>
          <span>Dilazioni create {{creati.length}}</span>
        </template>

        
        <div v-for="dilazione of creati" :key="dilazione.id" class="card">
          <div class="formgrid grid">
            <div class="field col-12 md:col-3">
              <label for="ragsoc" class="mr-2">Cliente</label>
              <router-link
                id="ragsoc"
                :to="`/ask/clienti/${dilazione.cliente.id}/dettagli`"
                custom
                v-slot="{ href }"
              >
                <Button 
                  :label="`${dilazione.cliente.ragsoc}`" 
                  class="p-button-info p-button-text w-full"
                  @click="navigateToTab(href)" 
                />
              </router-link>
            </div>
            <div class="field col-12 md:col-3">
              <label for="data_creazione" class="mr-2">Data Creazione</label>
              <InputText 
                id="data_creazione" 
                :value="dilazione.data_creazione ? moment(dilazione.data_creazione).format('DD/MM/YYYY') : 'Oggi'" 
                type="text" 
                :disabled="true" 
                class="inputfield w-full" 
              />
            </div>
            <div class="field col-12 md:col-3">
              <label for="numero_rate" class="mr-2">Numero Rate</label>
              <InputText 
                id="numero_rate" 
                v-model="dilazione.numero_rate" 

                type="text" 

                :disabled="true" 
                class="inputfield w-full" 
              />
            </div>
            <div class="field col-12 md:col-3">
              <label for="importo" class="mr-2">Importo Totale Dilazione</label>
              <InputNumber 
                id="importo" 
                v-model="dilazione.importo" 
                
                mode="currency" 
                currency="EUR" 
                locale="it-IT" 

                :disabled="true" 
                class="inputfield w-full" 
              />
            </div>
          </div>

          <Accordion :multiple="true" class="w-full">
            <AccordionTab header="Rate">
              <DataTable :value="dilazione.rate" responsiveLayout="scroll">
                <Column field="data" header="Data Pagamento">
                  <template #body="{ data: row }">
                    {{ (row.data !== null)? moment(row.data).format('DD/MM/YYYY'): '' }}
                  </template>
                </Column>
                <Column field="importo" header="Importo">
                  <template #body="{ data: row }">
                    {{ currencyFormatter.format( (row.importo !== null)? row.importo: 0 ) }}
                  </template>
                </Column>
              </DataTable>
            </AccordionTab>
          
            <AccordionTab header="Fatture">
              <div class="formgrid grid">
                <div v-for="fattura_numero of dilazione.fatture" class="field col-1" :key="fattura_numero">
                  <router-link
                    :to="`/ask/clienti/fatture/${fattura_numero}/dettagli`"
                    custom
                    v-slot="{ href }"
                  >
                    <Button 
                      :label="`${fattura_numero}`" 
                      class="p-button-info p-button-text"
                      @click="navigateToTab(href)" 
                    />
                  </router-link>
                </div>
              </div>
            </AccordionTab>
          </Accordion>
        </div>

      </AccordionTab>
    </Accordion>

    <div v-if="pendingPostalSlipRow" class="bollettino-confirm-overlay">
      <div class="bollettino-confirm" role="dialog" aria-modal="true">
        <h3>Operazione irreversibile</h3>
        <p>
          La fattura <strong>{{ pendingPostalSlipRow.numero }}</strong> passerà a Bollettino Postale.
          Il PDF verrà rigenerato con il bollettino allegato.
        </p>
        <p>
          Solo questa fattura cambia. Le successive restano RID, secondo la preferenza del cliente.
        </p>
        <div class="bollettino-confirm-actions">
          <Button
            label="Annulla"
            class="p-button-text"
            :disabled="!!changingInvoiceNumber"
            @click="cancelPostalSlipConfirm"
          />
          <Button
            label="Conferma"
            class="p-button-danger"
            :loading="changingInvoiceNumber === pendingPostalSlipRow.numero"
            :disabled="!!changingInvoiceNumber"
            @click="confirmPostalSlipChange"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import Loader from '@/components/Loader.vue'
import Service from '@/services/ask-management/dilazioni-service'

export default {
  props: {
    data: {
      type: Array,
      required: true
    }
  },
  
  components: {
    Loader,
  },

  data() {
    return {
      page:{
        name: 'fatture-vendita-aggiungi-dilazione',
      },

      dirty: false,

      moment,
      dataLoading: false,
      currencyFormatter: new Intl.NumberFormat('it-IT', {
        style: 'currency',
        currency: 'EUR',
      }), 

      watchers: [],

      fatturaNumeri: [],

      fatturaNumeriNo: [],
      changingInvoiceNumber: null,
      pendingPostalSlipRow: null,
      loadError: null,
      accordionActiveIndex: [],

      clienti: [],
      rimossi: [],
      creati:[],

      statoClienti: {},
    }
  },

  computed: {
    clientiPronti() {
      return this.clienti.filter((cliente) => Boolean(this.statoClienti[cliente.cod_cliente]))
    },

    hasVisibleContent() {
      return this.fatturaNumeriNo.length > 0
        || this.clientiPronti.length > 0
        || this.rimossi.length > 0
        || this.creati.length > 0
    },
  },

  created(){
    this.service = new Service(this.$http, this.$eventBus)
    this.syncFatturaNumeriFromProp()
  },

  mounted() {
    this.loadDilazioniData()
  },

  beforeUnmount() {
    if (this.dirty) {
      this.$eventBus.emit('incall:refresh-dilazioni', {
        source: this.page?.name,
        ts: Date.now(),
      })
    }
  },

  watch: {
    data: {
      deep: true,
      handler() {
        this.syncFatturaNumeriFromProp()
        this.loadDilazioniData()
      },
    },
  },

  methods:{

    syncFatturaNumeriFromProp() {
      this.fatturaNumeri = Array.isArray(this.data) ? [...this.data] : []
    },

    getClienteSelection(clienteId) {
      return this.statoClienti[clienteId]?.selected || []
    },

    setClienteSelection(clienteId, selection) {
      if (!this.statoClienti[clienteId]) {
        return
      }
      this.statoClienti[clienteId].selected = Array.isArray(selection) ? selection : []
    },

    updateAccordionActiveIndex() {
      const indexes = []
      let current = 0

      if (this.fatturaNumeriNo.length > 0) {
        indexes.push(current)
        current += 1
      }

      this.clientiPronti.forEach(() => {
        indexes.push(current)
        current += 1
      })

      if (this.rimossi.length > 0) {
        indexes.push(current)
        current += 1
      }

      if (this.creati.length > 0) {
        indexes.push(current)
      }

      this.accordionActiveIndex = indexes
    },

    onCancelButton(){
      this._reset()

      this.$eventBus.emit('dialog-return-cancel-button', {})
    },

    ////

    onCreateNewDilazione(clienteId){
      const stato = this.statoClienti[clienteId]
      const config = {
        cod_cliente: stato.cod_cliente,
        totale: Math.round( stato.rate.reduce((prev, curr) => (prev + curr.importo), 0) * 100 )/100,
        fatture: stato.selected.map( (el) => el.numero ),
        rate: stato.rate.map( (el) => {
          return {
            data: moment(el.data).format('YYYY-MM-DD'),
            importo: el.importo
          }
        } ),
        data_creazione: stato.data_creazione ? moment(stato.data_creazione).format('YYYY-MM-DD HH:mm:ss') : moment().format('YYYY-MM-DD HH:mm:ss')
      }

      this.service.makeDilazione(config).then((res) => {
        if(res.success){
          this.dirty = true
          this.clienti = this.clienti.filter((el) => (clienteId != el.cod_cliente))
        
          this.watchers[clienteId].unwatch()
          delete this.watchers[clienteId]
          delete this.statoClienti[clienteId]

          this.creati.push(res.item)
          this.updateAccordionActiveIndex()
          
          this.$emit('dilazione-creata', {
            success: true,
            dilazione: res.item,
            clienteId: clienteId
          })
        }
      })
    },

    onRemoveDilazione(clienteId){
      const i = this.clienti.findIndex((el) => (clienteId == el.cod_cliente))
      if(i >= 0){
        this.rimossi.push(this.clienti[i])

        this.clienti = this.clienti.filter((el) => (clienteId != el.cod_cliente))

        this.watchers[clienteId].unwatch()
        delete this.watchers[clienteId]
        delete this.statoClienti[clienteId]
        this.updateAccordionActiveIndex()

        this.dirty = true
        
        this.$emit('dilazione-rimossa', {
          success: true,
          clienteId: clienteId,
          cliente: this.rimossi[this.rimossi.length - 1]
        })
      }
    },

    onRestoreDilazione(clienteId){
      const i = this.rimossi.findIndex((el) => (clienteId == el.cod_cliente))
      if(i >= 0){
        const tmp = this.rimossi[i]

        this.clienti.unshift(tmp)
        this.rimossi = this.rimossi.filter((el) => (clienteId != el.cod_cliente))

        this.statoClienti[clienteId] = {
          cod_cliente: clienteId,
          selected: [...tmp.fatture],
          
          data_creazione: new Date(),
          data_inizio: new Date(),
          num_rate: 3,
          rate: [],
          saldo: 0,
          saldo_rate: 0,
        }

        this.watchers[clienteId] = {
          unwatch: this.$watch(
            () => this.statoClienti[clienteId].selected, 
            () => {
              this.updateClienteSaldi(clienteId)
              this.generateRates(clienteId)
            },
            { deep: true }
          )
        }

        this.updateClienteSaldi(clienteId)
        this.generateRates(clienteId)
        this.updateAccordionActiveIndex()
        this.dirty = true
      }
    },

    onChangeDataInizio(clienteId){
      this.updateClienteSaldi(clienteId)
      this.generateRates(clienteId)
    },

    onChangeRate(clienteId){
      this.updateClienteSaldi(clienteId)
      this.generateRates(clienteId)
    },

    onCellImportoEditComplete(event, idCliente){
      this.statoClienti[idCliente].rate[event.index] = event.newData
      this.updateClienteSaldi(idCliente)
    },

    ////

    _reset(){
      this.fatturaNumeri = []

      this.fatturaNumeriNo = []

      this.clienti = []    
      this.rimossi = []
      this.creati = []
      this.accordionActiveIndex = []

      for(const clienteId in this.statoClienti) {
        this.watchers[clienteId]?.unwatch?.()
        delete this.watchers[clienteId]
      }

      this.statoClienti = {}
    },

    navigateToTab(href){
      window.open(href, '_blank')
    },

    generateRates(clienteId){
      const stato = this.statoClienti[clienteId]
      if (!stato) return

      const saldo = Number(stato.saldo) || 0
      stato.rate = []
      for (let i = 0; i < stato.num_rate; i++) {
        if(i != stato.num_rate -1){
          stato.rate.push({
            id: i + 1,
            data: moment(stato.data_inizio).add(i, 'months').toDate(),
            importo: Math.round( (saldo / stato.num_rate) * 100 )/100
          })
        }else{
          let resto = saldo - Math.round( stato.rate.reduce((prev, curr) => (prev + curr.importo), 0) * 100 )/100

          stato.rate.push({
            id: i + 1,
            data: moment(stato.data_inizio).add(i, 'months').toDate(),
            importo: Math.round(resto * 100) /100
          })
        }
      }

      this.updateClienteSaldi(clienteId)
    },

    updateClienteSaldi(clienteId) {
      const stato = this.statoClienti[clienteId]
      if (!stato) return

      stato.saldo = Math.round(
        (stato.selected || []).reduce((prev, curr) => prev + (Number(curr.saldo) || 0), 0) * 100
      ) / 100

      stato.saldo_rate = Math.round(
        (stato.rate || []).reduce((prev, curr) => prev + (Number(curr.importo) || 0), 0) * 100
      ) / 100
    },

    formatClienteSaldo(codCliente) {
      const stato = this.statoClienti[codCliente]
      if (!stato) {
        return this.currencyFormatter.format(0)
      }
      return this.currencyFormatter.format(Number(stato.saldo) || 0)
    },

    buildClientiFromResponse(item) {
      if (!item || typeof item !== 'object' || Array.isArray(item)) {
        return []
      }

      return Object.entries(item)
        .map(([key, value]) => {
          const fattureList = Array.isArray(value?.fatture) ? value.fatture : []
          if (fattureList.length === 0) {
            return null
          }

          const cod_cliente = Number(fattureList[0]?.cod_cliente)
          if (!Number.isFinite(cod_cliente) || cod_cliente <= 0) {
            return null
          }

          const fatture = fattureList.map((el) => ({
            numero: el.numero,
            data_emissione: el.data_emissione,
            data_scadenza: el.data_scadenza,
            cod_type_pay: Number(el.cod_type_pay) || 0,
            payment_method: el.payment_method || '',
            saldo: el.saldo,
          }))

          return {
            cod_cliente,
            ragsoc: String(key || fattureList[0]?.cliente || '').trim(),
            fatture,
          }
        })
        .filter(Boolean)
    },

    initializeClientiState(clienti) {
      for (const clienteId in this.watchers) {
        this.watchers[clienteId]?.unwatch?.()
      }

      this.clienti = []
      this.statoClienti = {}
      this.watchers = {}

      const nextStato = {}
      const nextWatchers = {}

      clienti.forEach((cliente) => {
        const clienteId = cliente.cod_cliente
        nextStato[clienteId] = {
          cod_cliente: clienteId,
          selected: [...cliente.fatture],
          data_creazione: new Date(),
          data_inizio: new Date(),
          num_rate: 3,
          rate: [],
          saldo: 0,
          saldo_rate: 0,
        }

        nextWatchers[clienteId] = {
          unwatch: this.$watch(
            () => nextStato[clienteId].selected,
            () => {
              this.updateClienteSaldi(clienteId)
              this.generateRates(clienteId)
            },
            { deep: true }
          )
        }
      })

      this.statoClienti = nextStato
      this.watchers = nextWatchers
      this.clienti = clienti

      clienti.forEach((cliente) => {
        this.updateClienteSaldi(cliente.cod_cliente)
        this.generateRates(cliente.cod_cliente)
      })
    },

    loadDilazioniData() {
      this.getAllDilazioniNo()
    },

    isMoreThanEightDaysBeforeDueDate(dueDate) {
      const due = moment(dueDate).startOf('day')
      if (!dueDate || !due.isValid()) {
        return false
      }

      const today = moment().startOf('day')
      const blockedFrom = due.clone().subtract(8, 'days')
      return today.isBefore(blockedFrom, 'day')
    },

    canChangeToPostalSlip(row) {
      const balance = Number(row?.saldo)
      return Number.isFinite(balance)
        && balance > 0
        && Number(row?.cod_type_pay) === 3
        && this.isMoreThanEightDaysBeforeDueDate(row?.data_scadenza)
    },

    askPostalSlipConfirm(row) {
      if (!this.canChangeToPostalSlip(row) || this.changingInvoiceNumber) {
        return
      }
      this.pendingPostalSlipRow = row
    },

    cancelPostalSlipConfirm() {
      if (this.changingInvoiceNumber) {
        return
      }
      this.pendingPostalSlipRow = null
    },

    async confirmPostalSlipChange() {
      const row = this.pendingPostalSlipRow
      if (!row) {
        return
      }
      await this.changeToPostalSlip(row)
    },

    async changeToPostalSlip(row) {
      if (!this.canChangeToPostalSlip(row) || this.changingInvoiceNumber) {
        return
      }

      this.changingInvoiceNumber = row.numero
      try {
        const res = await this.service.setPaymentMethodToPostalSlip(row.numero)
        if (!res.success) {
          this.$toast?.add({
            severity: 'error',
            summary: 'Errore',
            detail: res.item?.message || 'Impossibile cambiare il metodo di pagamento',
            life: 4000,
          })
          return
        }

        row.cod_type_pay = 2
        row.payment_method = res.item?.payment_method || 'Bollettino Postale'
        this.pendingPostalSlipRow = null
        this.$toast?.add({
          severity: 'success',
          summary: 'Metodo aggiornato',
          detail: `Fattura ${row.numero} impostata su Bollettino Postale`,
          life: 3000,
        })
      } finally {
        this.changingInvoiceNumber = null
      }
    },

    getAllDilazioniNo(){
      this.dataLoading = true
      this.loadError = null

      const numeri = Array.isArray(this.fatturaNumeri) ? this.fatturaNumeri : []

      if (numeri.length === 0) {
        this.loadError = 'Nessuna fattura selezionata per la dilazione.'
        this.dataLoading = false
        return
      }

      this.service.getAllDilazioniNo({'fattura_vendita.numeri': numeri})
      .then((res) => {
        try {
          const nextClienti = this.buildClientiFromResponse(res?.item)
          this.initializeClientiState(nextClienti)

          const fatture = nextClienti
            .map((el) => el.fatture.map((e) => e.numero))
            .reduce((prev, curr) => [...prev, ...curr], [])

          this.fatturaNumeriNo = numeri.filter((x) => !fatture.includes(x))
          this.updateAccordionActiveIndex()

          if (nextClienti.length === 0 && this.fatturaNumeriNo.length === 0) {
            this.loadError = 'Nessuna fattura dilazionabile trovata per questo cliente.'
          }
        } catch (error) {
          console.error('Errore inizializzazione dilazione:', error)
          this.loadError = 'Errore nella preparazione dei dati per la dilazione.'
          this.clienti = []
          this.statoClienti = {}
        }
      })
      .catch((err) => {
        console.error('Errore in getAllDilazioniNo:', err)
        this.loadError = 'Errore nel caricamento delle fatture dilazionabili.'
        this.clienti = []
        this.statoClienti = {}
        if (this.$toast) {
          this.$toast.add({
            severity: 'error',
            summary: 'Errore',
            detail: this.loadError,
            life: 5000
          })
        }
      })
      .finally(() =>{
        this.dataLoading = false
      })
    },

  },
}
</script>

<style scoped>
.pay-method-cell {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  white-space: nowrap;
}

.pay-method-change-btn {
  flex: 0 0 auto;
}

.bollettino-confirm-overlay {
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.45);
}

.bollettino-confirm {
  width: min(32rem, calc(100% - 2rem));
  padding: 1.5rem;
  background: #fff;
  border-radius: 8px;
}

.bollettino-confirm h3 {
  margin: 0 0 0.75rem;
}

.bollettino-confirm p {
  margin: 0 0 0.75rem;
  line-height: 1.45;
}

.bollettino-confirm-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1.25rem;
}
</style>
